/**
 * THE COST CURVE — cumulative spend over time, for the budget burn-up.
 *
 * The arithmetic behind the budget graph, kept out of the drawing so it can be
 * tested without a browser (the same split as burndown.ts / Burndown.tsx). It
 * walks the ledger's dated lines in date order and accumulates PAID and
 * COMMITTED separately, so the graph can show paid climbing with committed
 * stacked on top, read against the approved budget and the forecast.
 *
 * DATED LINES ONLY ON THE LINE. A line with no date cannot sit on a time axis,
 * so it is counted into `undated` and reported as a caveat rather than smeared
 * across the curve — the same honesty the burndown keeps for unsized cards. The
 * total band already carries the true grand total; this is only the shape.
 */
import type { Project, InvoiceLine, BudgetContext } from './projects';
import { lineBaseAmount } from './projects';

/** A day-number (days since the epoch), so a line's 'YYYY-MM-DD' and the
    project's millisecond go-live can share one x-axis. */
export function dayNum(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const d = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(d) ? Math.floor(d / 86400000) : null;
}

/** A running total as of one date — cumulative, not that date's own spend. */
export interface CostStep {
  day: number;
  date: string;
  paid: number;
  committed: number;
}

export interface CostCurve {
  /** One step per distinct dated day, ascending, holding the cumulative totals. */
  steps: CostStep[];
  budget?: number;
  forecast?: number;
  /** The left and right edges of the axis, in day-numbers. */
  from?: number;
  to?: number;
  /** The go-live, as a day-number, when it sits within/after the range. */
  goLiveDay?: number;
  /** Lines with no usable date — in the totals, not on the line. */
  undated: { paid: number; committed: number; count: number };
}

/**
 * Build the cost curve for a project. Returns steps in date order; when there
 * are no dated lines the steps are empty and the caller draws nothing.
 */
export function costCurve(
  project: Pick<Project, 'budget' | 'forecast' | 'goLive' | 'invoices'>,
  ctx?: BudgetContext,
): CostCurve {
  const lines: InvoiceLine[] = project.invoices ?? [];
  const base = ctx?.base ?? '';
  // Every amount is converted to the base currency before it reaches the curve,
  // by the same rule as the rollup (paid frozen, committed at the current rate).
  const inBase = (l: InvoiceLine) => lineBaseAmount(l, base, ctx?.rates);
  const undated = { paid: 0, committed: 0, count: 0 };

  // Split the dated from the undated, and keep the dated in date order.
  const dated: { day: number; date: string; line: InvoiceLine }[] = [];
  for (const l of lines) {
    const day = l.date ? dayNum(l.date) : null;
    if (day == null) {
      undated.count += 1;
      if (l.status === 'paid') undated.paid += inBase(l);
      else undated.committed += inBase(l);
      continue;
    }
    dated.push({ day, date: l.date!, line: l });
  }
  dated.sort((a, b) => a.day - b.day || (a.date < b.date ? -1 : 1));

  // Accumulate, collapsing every line that shares a day into one step.
  const steps: CostStep[] = [];
  let paid = 0;
  let committed = 0;
  for (const { day, date, line } of dated) {
    if (line.status === 'paid') paid += inBase(line);
    else committed += inBase(line);
    const last = steps[steps.length - 1];
    if (last && last.day === day) {
      last.paid = paid;
      last.committed = committed;
    } else {
      steps.push({ day, date, paid, committed });
    }
  }

  const goLiveDay = project.goLive != null ? Math.floor(project.goLive / 86400000) : undefined;

  let from: number | undefined;
  let to: number | undefined;
  if (steps.length) {
    from = steps[0].day;
    to = steps[steps.length - 1].day;
    // The go-live extends the axis to the right when it is still ahead, so the
    // curve is read against the runway that is left, not just where it has got to.
    if (goLiveDay != null && goLiveDay > to) to = goLiveDay;
    // A single dated day has no width; give it a little so the point is drawable.
    if (from === to) {
      from -= 1;
      to += 1;
    }
  }

  return {
    steps,
    budget: project.budget,
    forecast: project.forecast,
    from,
    to,
    goLiveDay: goLiveDay != null && from != null && to != null && goLiveDay >= from && goLiveDay <= to ? goLiveDay : undefined,
    undated,
  };
}
